/*
 * The wire contract between this dashboard and whatever serves it.
 *
 * Nothing here is invented for the mock generator's convenience — every field
 * is something a browser client can actually report without collecting
 * personal data. `browser/telemetry.py` (desktop) and `src/services/
 * telemetry.js` (mobile) emit the raw events these aggregate from; see
 * docs/INGEST_API.md for the endpoint list.
 */

/**
 * Platforms are reported at OS level, not as "desktop vs mobile". The desktop
 * client runs on three very different operating systems whose crash rates,
 * performance and feature use diverge sharply — collapsing them hides exactly
 * the differences worth acting on.
 */
export type Platform = 'windows' | 'macos' | 'linux' | 'android' | 'ios';

export const PLATFORMS: Platform[] = ['windows', 'macos', 'linux', 'android', 'ios'];

/** Which codebase a platform ships from. */
export type ClientKind = 'desktop' | 'mobile';

export const PLATFORM_CLIENT: Record<Platform, ClientKind> = {
  windows: 'desktop',
  macos: 'desktop',
  linux: 'desktop',
  android: 'mobile',
  ios: 'mobile',
};

export const PLATFORM_LABELS: Record<Platform, string> = {
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
  android: 'Android',
  ios: 'iOS',
};

/** A per-platform bag of numbers. Every platform key is always present. */
export type ByPlatform<T = number> = Record<Platform, T>;

/** Days of history the dashboard asks for. */
export type RangeKey = '1d' | '7d' | '1m' | '3m' | '1y' | 'lifetime';

export interface RangeOption {
  key: RangeKey;
  label: string;
  /** Full name, for the control's tooltip — the button itself stays compact. */
  title: string;
  /**
   * Days of history. For `lifetime` this is only what the mock generator draws;
   * the live service measures the real span from the earliest install instead.
   */
  days: number;
}

/**
 * Keys are the wire values — `server/src/snapshot.js` has the same table and the
 * two must not drift, or the service rejects the request outright.
 */
export const RANGES: RangeOption[] = [
  { key: '1d', label: '1D', title: '1 day', days: 1 },
  { key: '7d', label: '7D', title: '7 days', days: 7 },
  { key: '1m', label: '1M', title: '1 month', days: 30 },
  { key: '3m', label: '3M', title: '3 months', days: 90 },
  { key: '1y', label: '1Y', title: '1 year', days: 365 },
  { key: 'lifetime', label: 'Lifetime', title: 'Everything on record', days: 120 },
];

export const DEFAULT_RANGE: RangeKey = '1d';

/** True for a value that is still a range the dashboard offers. */
export function isRangeKey(value: unknown): value is RangeKey {
  return RANGES.some((r) => r.key === value);
}

/**
 * What the header filter can be set to: everything, one client's platforms, or
 * a single platform.
 */
export type PlatformFilter = 'all' | ClientKind | Platform;

/** Every value the header filter accepts, for validating restored state. */
export const PLATFORM_FILTERS: PlatformFilter[] = [
  'all',
  'desktop',
  'mobile',
  ...PLATFORMS,
];

export function isPlatformFilter(value: unknown): value is PlatformFilter {
  return PLATFORM_FILTERS.includes(value as PlatformFilter);
}

/** Expands a filter to the concrete platforms it covers. */
export function platformsFor(filter: PlatformFilter): Platform[] {
  if (filter === 'all') return PLATFORMS;
  if (filter === 'desktop' || filter === 'mobile') {
    return PLATFORMS.filter((p) => PLATFORM_CLIENT[p] === filter);
  }
  return [filter];
}

/* ── Time series ────────────────────────────────────────────────────── */

export interface DayPoint extends ByPlatform {
  /** ISO date, `YYYY-MM-DD`. */
  date: string;
}

export interface ActiveUsersSeries {
  dau: DayPoint[];
  wau: DayPoint[];
  mau: DayPoint[];
  /** New installs per day. */
  installs: DayPoint[];
  /** Users whose last-seen fell outside the churn window that day. */
  churned: DayPoint[];
}

/* ── Summary ────────────────────────────────────────────────────────── */

export interface Metric {
  value: number;
  /** Fractional change vs the immediately preceding window (0.081 = +8.1%). */
  delta: number;
  /** Sparkline history, oldest → newest. */
  history: number[];
}

export interface SummaryMetrics {
  dau: Metric;
  mau: Metric;
  newUsers: Metric;
  /** DAU/MAU. 0–1. */
  stickiness: Metric;
  /** Mean seconds per session. */
  avgSessionSec: Metric;
  /** Sessions per active user per day. */
  sessionsPerUser: Metric;
  /** Share of sessions with no fatal error. 0–1. */
  crashFreeRate: Metric;
  /** Page loads across the fleet. */
  pageLoads: Metric;
}

/* ── Breakdowns ─────────────────────────────────────────────────────── */

export interface Segment extends ByPlatform {
  name: string;
}

export interface OsVersionRow extends ByPlatform {
  /** e.g. "Windows 11", "macOS 15", "Ubuntu 24.04". */
  name: string;
  platform: Platform;
}

export interface VersionRow {
  version: string;
  platform: Platform;
  users: number;
  /** Share of that platform's users. 0–1. */
  share: number;
  crashFreeRate: number;
  releasedOn: string;
  isLatest: boolean;
}

export interface FeatureRow {
  id: string;
  name: string;
  /** Grouping used on the Features page. */
  category: 'Privacy' | 'Tabs' | 'AI' | 'Productivity' | 'Media' | 'Sync';
  /**
   * Fraction of that platform's active users who used the feature at least
   * once in the window, or null where the feature does not exist there.
   */
  adoption: ByPlatform<number | null>;
  /** Uses per adopting user per week. */
  frequency: number;
  /** Fractional change in adoption vs the previous window. */
  trend: number;
}

export interface RetentionCohort {
  /** Cohort start date, ISO. */
  cohort: string;
  size: number;
  /** Retention by day offset — index 0 is day 0 and is always 1. */
  days: (number | null)[];
}

export interface ErrorRow {
  id: string;
  title: string;
  platform: Platform;
  events: number;
  users: number;
  firstSeen: string;
  lastSeen: string;
  version: string;
  severity: 'critical' | 'serious' | 'warning';
}

export interface PerfMetric {
  name: string;
  unit: 'ms' | 's' | 'MB';
  byPlatform: ByPlatform;
  /** Target the team holds itself to; drives the meter track. */
  budget: number;
  /** Lower is better for every metric currently tracked. */
  lowerIsBetter: true;
}

export interface GeoRow {
  country: string;
  code: string;
  users: number;
  share: number;
}

export interface EngagementBuckets {
  /** Session-length histogram. */
  sessionLength: (ByPlatform & { bucket: string })[];
  /** Local hour-of-day 0–23 × weekday 0–6 usage grid. */
  hourly: { hour: number; weekday: number; sessions: number }[];
}

/* ── The whole payload ──────────────────────────────────────────────── */

export interface Snapshot {
  generatedAt: string;
  range: RangeKey;
  /** Set false by the REST adapter; true when numbers come from the generator. */
  isMock: boolean;
  liveNow: ByPlatform;
  summary: SummaryMetrics;
  activeUsers: ActiveUsersSeries;
  /** OS releases within each platform, e.g. Windows 11 vs Windows 10. */
  osVersions: OsVersionRow[];
  versions: VersionRow[];
  features: FeatureRow[];
  retention: RetentionCohort[];
  errors: ErrorRow[];
  perf: PerfMetric[];
  geo: GeoRow[];
  engagement: EngagementBuckets;
  themes: Segment[];
  searchEngines: Segment[];
}

/* ── Helpers shared by the pages ────────────────────────────────────── */

/** Sums the platforms the filter covers. */
export function sumFor(row: Partial<ByPlatform>, filter: PlatformFilter): number {
  return platformsFor(filter).reduce((total, p) => total + (row[p] ?? 0), 0);
}

/** Empty per-platform bag, for building rows up. */
export function zeroByPlatform(): ByPlatform {
  return { windows: 0, macos: 0, linux: 0, android: 0, ios: 0 };
}
